import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Clock, Mail, FileCheck, Code, Settings,
  Database, Activity, Shield, Globe, FileText,
  ChevronDown, ChevronRight, Search, ShieldCheck, Upload, Layout,
  Home, Package, Users, CheckSquare, Zap, GitBranch, Workflow, Trash2, Lock, Menu
} from 'lucide-react';
import { buildAdminPath } from '../../../utils/adminPaths';

// ============================================
// INTERFACES
// ============================================

interface SidebarItemProps {
  id: string;
  label: string;
  icon: any;
  isActive: boolean;
  isPlaceholder?: boolean;
}

interface SidebarSectionProps {
  id: string;
  label: string;
  isExpanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

interface AdminSidebarProps {
  activeSection: string;
  onNavigate?: (section: string) => void;
  collapsed: boolean;
}

interface NavigationItem {
  id: string;
  label: string;
  icon: any;
  isPlaceholder?: boolean;
}

interface NavigationSection {
  id: string;
  label: string;
  items: NavigationItem[];
}

// ============================================
// SUB-COMPONENTS
// ============================================

const SidebarSection: React.FC<SidebarSectionProps> = ({
  label,
  isExpanded,
  onToggle,
  children
}) => {
  return (
    <div className="mb-4">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 transition-colors"
        aria-expanded={isExpanded}
        aria-label={`Toggle ${label} section`}
      >
        <span>{label}</span>
        {isExpanded ? (
          <ChevronDown className="h-4 w-4" />
        ) : (
          <ChevronRight className="h-4 w-4" />
        )}
      </button>
      {isExpanded && (
        <div className="mt-2 space-y-1">
          {children}
        </div>
      )}
    </div>
  );
};

const SidebarItem: React.FC<SidebarItemProps> = ({
  id,
  label,
  icon: Icon,
  isActive,
  isPlaceholder
}) => {
  const to = buildAdminPath(id);
  const className = `w-full flex items-center px-3 py-2 text-sm rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 ${
    isActive
      ? 'bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 font-medium'
      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
  }`;
  const content = (
    <>
      <Icon className={`h-5 w-5 mr-3 ${isActive ? 'text-purple-600 dark:text-purple-400' : 'text-gray-500 dark:text-gray-400'}`} />
      <span className="flex-1 text-left">{label}</span>
      {isPlaceholder && (
        <span className="text-xs px-2 py-0.5 bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded">
          Soon
        </span>
      )}
    </>
  );
  if (isPlaceholder) {
    return <span className={className}>{content}</span>;
  }
  return (
    <Link to={to} className={className} aria-label={label} aria-current={isActive ? 'page' : undefined}>
      {content}
    </Link>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================

const AdminSidebar: React.FC<AdminSidebarProps> = ({
  activeSection,
  onNavigate,
  collapsed
}) => {
  // State for expanded sections - Administration starts expanded
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    administration: true,
    'ui-configuration': false,
    'process-automation': false,
    'data-management': false,
    advanced: false
  });

  const [quickFindQuery, setQuickFindQuery] = useState('');

  // Navigation sections organized by category
  const navigationSections: NavigationSection[] = [
    {
      id: 'administration',
      label: 'Administration',
      items: [
        { id: 'users', label: 'Users', icon: Users },
        { id: 'roles', label: 'Roles & Permissions', icon: Shield },
        { id: 'security-access', label: 'Security & Access', icon: ShieldCheck },
        { id: 'email-templates', label: 'Email Templates', icon: Mail },
        { id: 'notes-admin', label: 'Notes Management', icon: FileCheck }
      ]
    },
    {
      id: 'ui-configuration',
      label: 'UI Configuration',
      items: [
        { id: 'app-settings', label: 'App Settings', icon: Settings },
        { id: 'component-library', label: 'Component Library', icon: Layout },
        { id: 'system-settings', label: 'Settings', icon: Settings },
        { id: 'navigation', label: 'Navigation', icon: Menu }
      ]
    },
    {
      id: 'process-automation',
      label: 'Process Automation',
      items: [
        { id: 'workflow-rules', label: 'Workflow Rules', icon: Zap },
        { id: 'approval-processes', label: 'Approval Processes', icon: CheckSquare },
        { id: 'process-builder', label: 'Process Builder', icon: GitBranch },
        { id: 'flow-automation', label: 'Flow Automation', icon: Workflow },
        { id: 'scheduled-jobs', label: 'Scheduled Jobs', icon: Clock }
      ]
    },
    {
      id: 'data-management',
      label: 'Data Management',
      items: [
        { id: 'data-sensitivity', label: 'Data Sensitivity', icon: ShieldCheck },
        { id: 'data-retention', label: 'Data Retention', icon: Clock },
        { id: 'bulk-data', label: 'Bulk Data Operations', icon: Upload },
        { id: 'mass-delete', label: 'Mass Delete Records', icon: Trash2 }
      ]
    },
    {
      id: 'advanced',
      label: 'Advanced',
      items: [
        { id: 'database', label: 'Database', icon: Database, isPlaceholder: true },
        { id: 'monitoring', label: 'Monitoring', icon: Activity, isPlaceholder: true },
        { id: 'system-security', label: 'System Security', icon: Lock, isPlaceholder: true },
        { id: 'api', label: 'API Management', icon: Globe, isPlaceholder: true },
        { id: 'logs', label: 'System Logs', icon: FileText, isPlaceholder: true }
      ]
    }
  ];

  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  // Filter sections based on quick find query
  const filteredSections = navigationSections.map(section => ({
    ...section,
    items: section.items.filter(item =>
      quickFindQuery === '' ||
      item.label.toLowerCase().includes(quickFindQuery.toLowerCase())
    )
  })).filter(section => section.items.length > 0);

  return (
    <aside
      className={`fixed left-0 top-16 bottom-0 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 transition-all duration-300 ${
        collapsed ? 'w-16' : 'w-64'
      } overflow-y-auto`}
      aria-label="Admin navigation"
    >
      {/* Quick Find Search */}
      {!collapsed && (
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="text"
              value={quickFindQuery}
              onChange={(e) => setQuickFindQuery(e.target.value)}
              placeholder="Quick find..."
              className="block w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-700 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              aria-label="Quick find navigation"
            />
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className="p-2">
        {/* Setup Home - Standalone */}
        {!collapsed && (
          <Link
            to={buildAdminPath('setup-home')}
            className={`w-full text-left px-4 py-3 flex items-center space-x-3 rounded-lg mb-4 transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 ${
              activeSection === 'setup-home'
                ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 font-medium'
                : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
            aria-label="Setup Home"
            aria-current={activeSection === 'setup-home' ? 'page' : undefined}
          >
            <Home className="w-5 h-5" />
            <span className="font-medium">Setup Home</span>
          </Link>
        )}

        {/* Object Manager - Standalone */}
        {!collapsed && (
          <Link
            to={buildAdminPath('object-manager')}
            className={`w-full text-left px-4 py-3 flex items-center space-x-3 rounded-lg mb-6 transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 ${
              activeSection === 'object-manager'
                ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 font-medium'
                : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
            aria-label="Object Manager"
            aria-current={activeSection === 'object-manager' ? 'page' : undefined}
          >
            <Package className="w-5 h-5" />
            <span className="font-medium">Object Manager</span>
          </Link>
        )}

        {/* Divider line before sections */}
        {!collapsed && (
          <div className="border-t border-gray-200 dark:border-gray-700 mb-4" />
        )}

        {/* Collapsible Navigation Sections */}
        {!collapsed && filteredSections.map((section) => (
          <SidebarSection
            key={section.id}
            id={section.id}
            label={section.label}
            isExpanded={expandedSections[section.id] || false}
            onToggle={() => toggleSection(section.id)}
          >
            {section.items.map((item) => (
              <SidebarItem
                key={item.id}
                id={item.id}
                label={item.label}
                icon={item.icon}
                isActive={activeSection === item.id}
                isPlaceholder={item.isPlaceholder}
              />
            ))}
          </SidebarSection>
        ))}

        {/* Collapsed mode - show all items without sections */}
        {collapsed && navigationSections.flatMap(section => section.items).map((item) => {
          const Icon = item.icon;
          const isActive = activeSection === item.id;
          const className = `w-full flex items-center justify-center px-3 py-2 text-sm rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 mb-1 ${
            isActive
              ? 'bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300'
              : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
          }`;

          if (item.isPlaceholder) {
            return (
              <span key={item.id} className={className} title={item.label}>
                <Icon className={`h-5 w-5 ${isActive ? 'text-purple-600 dark:text-purple-400' : 'text-gray-500 dark:text-gray-400'}`} />
              </span>
            );
          }
          return (
            <Link
              key={item.id}
              to={buildAdminPath(item.id)}
              className={className}
              aria-label={item.label}
              aria-current={isActive ? 'page' : undefined}
              title={item.label}
            >
              <Icon className={`h-5 w-5 ${isActive ? 'text-purple-600 dark:text-purple-400' : 'text-gray-500 dark:text-gray-400'}`} />
            </Link>
          );
        })}
      </nav>
    </aside>
  );
};

export default AdminSidebar;
